import React, { useRef, useState, useEffect } from "react";
import { toDisplayDate } from '@/lib/orgFormat';
import { useTranslation } from 'react-i18next';
import {
  getMyProfile, getCachedProfile, updateEmployee, getDepartments, uploadProfilePicture,
  uploadDocument, getDocuments,
  getAddresses, addAddress, getEmergencyContacts, addEmergencyContact, getEducation, addEducation,
  updateAddress, deleteAddress, updateEmergencyContact, deleteEmergencyContact, updateEducation, deleteEducation,
  getMyChangeRequests,
} from '@/lib/hrApi';
import { AvatarCropper } from './AvatarCropper';
import toast from 'react-hot-toast';
import {
  useMyPaymentMethods, useAddPaymentMethod, useUpdatePaymentMethod,
  useDeactivatePaymentMethod, useSetPrimary, useSetMyDefault,
} from '@/features/payment-methods/api/hooks';
import { PaymentMethodChips, PayoutHoldBanner, sortForDisplay, canBeDefault, canDeactivate } from '@/features/payment-methods/components/PaymentMethodChips';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useHasPermission } from '@/rbac/useRbac';
import { isValidPhone, PHONE_HINT } from '@/features/shared/validation/phone';
import { isValidTIN, isValidNationalId, isValidNSSF, TIN_HINT, NATIONAL_ID_HINT, NSSF_HINT } from '@/features/shared/validation/statutory';
import { PhoneInput } from '@/features/shared/components/PhoneInput';
import { useAppDispatch } from '@/app/hooks';
import { updateUser } from '@/features/auth/state/auth-state/authSlice';
import { useUnsavedGuard, usePendingGuard } from '@/features/shared/components/recovery/useFormGuards';
import { mayApplyRefresh } from '@/features/shared/components/recovery/refreshSafety';
import { 
  User, 
  Calendar, 
  Clock,
  FileText,
  MapPin,
  Phone,
  AlertTriangle,
  GraduationCap,
  Folder,
  Edit,
  Save,
  Check,
  Star,
  Shield,
  Award,
  Mail,
  Globe,
  Briefcase,
  Wallet
} from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MOBILE_MONEY_PROVIDERS = ['M-Pesa', 'TigoPesa', 'AirtelMoney', 'HaloPesa', 'AzamPesa', 'Other'];

// Presentational read-only row: a label with its value as plain text (no input
// box), so non-edit views don't look editable. Empty values render an em dash.
function ReadField({ label, value, className = '' }: { label: string; value?: React.ReactNode; className?: string }) {
  const empty = value === undefined || value === null || value === '' || (typeof value === 'string' && !value.trim());
  return (
    <div className={className}>
      <p className="text-sm font-medium text-muted-foreground mb-1">{label}</p>
      <p className={`text-base ${empty ? 'text-muted-foreground/60' : 'text-foreground'}`}>{empty ? '—' : value}</p>
    </div>
  );
}

// What the person proposed for a statutory id that HR has not approved yet.
// Without this the field reads "—" after a reload and the submission looks
// lost — the value is on the change request, not on the employee record.
function PendingValue({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <p className="mt-1 text-xs text-orange-700 dark:text-orange-300" data-testid="pending-id-value">
      {label}: <span className="font-medium">{value}</span>
    </p>
  );
}

// employee-service field key → the label this page shows for it.
const FIELD_LABELS: Record<string, string> = {
  national_id: 'National ID Number', nssf: 'NSSF Number', tin: 'TIN Number',
  driver_license: "Driver's License", passport_number: 'Passport number',
  work_permit_number: 'Work permit number', resident_permit_number: 'Resident permit number',
  local_government_id: 'Local government ID', wcf_number: 'WCF number',
  first_name: 'First name', middle_name: 'Middle name', last_name: 'Last name',
  dob: 'Date of birth', email: 'Email',
};
const fieldLabel = (f: string) => FIELD_LABELS[f] || f.replace(/_/g, ' ');

// Official Tanzania regions (must match the employee-service address validator).
const TZ_REGIONS = [
  'Arusha', 'Dar es Salaam', 'Dodoma', 'Geita', 'Iringa', 'Kagera', 'Katavi', 'Kigoma',
  'Kilimanjaro', 'Lindi', 'Manyara', 'Mara', 'Mbeya', 'Morogoro', 'Mtwara', 'Mwanza',
  'Njombe', 'Pemba North', 'Pemba South', 'Pwani', 'Rukwa', 'Ruvuma', 'Shinyanga', 'Simiyu',
  'Singida', 'Songwe', 'Tabora', 'Tanga', 'Unguja North', 'Unguja South',
];

interface TabConfig {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  bgGradient: string;
}

const getTabsConfig = (t: any): TabConfig[] => [
  { 
    id: 'basic', 
    label: t('tabs.basic_info'), 
    icon: User, 
    color: 'orange',
    bgGradient: 'from-orange-500 to-amber-600'
  },
  { 
    id: 'address', 
    label: t('tabs.address'), 
    icon: MapPin, 
    color: 'orange',
    bgGradient: 'from-amber-500 to-orange-600'
  },
  { 
    id: 'emergency', 
    label: t('tabs.emergency_contact'), 
    icon: AlertTriangle, 
    color: 'orange',
    bgGradient: 'from-orange-600 to-amber-600'
  },
  { 
    id: 'education', 
    label: t('tabs.education'), 
    icon: GraduationCap, 
    color: 'orange',
    bgGradient: 'from-amber-600 to-orange-500'
  },
  {
    id: 'payment',
    label: t('tabs.payment', { defaultValue: 'Payment Details' }),
    icon: Wallet,
    color: 'orange',
    bgGradient: 'from-amber-500 to-orange-500'
  },
  {
    id: 'documents',
    label: t('tabs.documents'),
    icon: Folder,
    color: 'orange',
    bgGradient: 'from-orange-500 to-amber-500'
  },
];

export function PersonalProfile() {
  const { t } = useTranslation('profile');
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState('basic');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Compared against the loaded record, not against empty — see applyProfile.
  // null until the first load, so a page that has not loaded yet is never
  // treated as edited.
  const baselineRef = useRef<string | null>(null);
  usePendingGuard(saving, 'personal-profile', 'your profile details');
  const [error, setError] = useState<string | null>(null);
  const [empId, setEmpId] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);

  // Sub-resources embedded in the profile response (address/emergency/education/docs).
  const [addresses, setAddresses] = useState<any[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<any[]>([]);
  const [educationRecords, setEducationRecords] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);

  // Self-service add/edit forms for personal sub-resources (address/emergency/education/payment).
  const [addOpen, setAddOpen] = useState<null | 'address' | 'emergency' | 'education' | 'payment'>(null);
  const [editId, setEditId] = useState<string | null>(null); // null = adding, id = editing
  const [savingChild, setSavingChild] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ kind: 'address' | 'emergency' | 'education' | 'payment'; id: string } | null>(null);
  const blankAddress = { street: '', city: '', region: '', postal_code: '', district: '', ward: '', country: 'Tanzania' };
  const blankEmergency = { name: '', first_name: '', middle_name: '', last_name: '', preferred_name: '', relationship: '', phone: '', alternative_phone: '', email: '' };
  const blankEducation = { institution: '', degree: '', field: '', year: '' };
  const blankPayment = {
    type: 'BANK' as 'BANK' | 'MOBILE_MONEY',
    bank_name: '', branch: '', account_holder_name: '', account_number: '',
    mobile_money_provider: 'M-Pesa', phone_number: '',
    country: 'Tanzania', currency: 'TZS', is_primary: false,
  };
  const [newAddress, setNewAddress] = useState({ ...blankAddress });
  const [newEmergency, setNewEmergency] = useState({ ...blankEmergency });
  const [newEducation, setNewEducation] = useState({ ...blankEducation });
  const [newPayment, setNewPayment] = useState({ ...blankPayment });

  // Payout methods come from a dedicated query (not the profile response).
  const paymentMethodsQuery = useMyPaymentMethods();
  const paymentMethods = sortForDisplay(paymentMethodsQuery.data ?? []);
  const addPayment = useAddPaymentMethod();
  const updatePayment = useUpdatePaymentMethod();
  const deactivatePayment = useDeactivatePaymentMethod();
  const setPrimaryPayment = useSetPrimary();
  const setDefaultPayment = useSetMyDefault();
  const confirm = useConfirm();
  const savingPayment = addPayment.isPending || updatePayment.isPending;

  const tabs = getTabsConfig(t);

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    preferredName: "",
    email: "",
    phone: "",
    dob: "",
    showBirthday: false,
    nationality: "",
    maritalStatus: "",
    nationalId: "",
    nssf: "",
    tin: "",
    driverLicense: "",
    jobTitle: "",
    department: "",
    employeeId: "",
    joinDate: "",
    profilePictureUrl: "",
    status: "Active",
  });

  // applyProfile runs outside render and needs the CURRENT form, not the one
  // captured when it was defined.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const profileDirty = baselineRef.current !== null
    && JSON.stringify(formData) !== baselineRef.current;
  useUnsavedGuard(profileDirty, 'personal-profile', 'your profile details');

  // Display name = first + middle + last (a preferred name, if set, is what we
  // greet them by). Structured fields are the source of truth; this is derived.
  const displayFullName = [formData.firstName, formData.middleName, formData.lastName]
    .filter(Boolean).join(' ').trim();
  const greetingName = formData.preferredName?.trim() || displayFullName;

  // Profile completion = % of key fields that are filled.
  const completionFields = [
    formData.firstName, formData.lastName, formData.email, formData.phone, formData.dob,
    formData.nationality, formData.maritalStatus, formData.nationalId, formData.jobTitle,
  ];
  const profileCompletion = Math.round(
    (completionFields.filter((v) => v && String(v).trim()).length / completionFields.length) * 100,
  );

  // Map a profile record into the page state.
  const applyProfile = (e: any) => {
    setEmpId(e.id);
    // ⚠️ Snapshot what the server gave us, so "dirty" means "differs from
    // what was loaded" rather than "is non-empty". Without a baseline, a
    // fully populated profile would look permanently unsaved and warn on
    // every navigation — noise that trains people to dismiss the dialog.
    const loaded = {
      firstName: e.first_name ?? '',
      middleName: e.middle_name ?? '',
      lastName: e.last_name ?? '',
      preferredName: e.preferred_name ?? '',
      email: e.email ?? '',
      phone: e.phone ?? '',
      dob: (e.dob ?? '').slice(0, 10), // YYYY-MM-DD for the native date input
      showBirthday: !!e.show_birthday,
      nationality: e.nationality ?? '',
      maritalStatus: e.marital_status ?? '',
      nationalId: e.national_id ?? '',
      nssf: e.nssf ?? '',
      tin: e.tin ?? '',
      driverLicense: e.driver_license ?? '',
      jobTitle: e.position ?? '',
      department: e.department?.name || (e.department_id ? 'Assigned' : 'Unassigned'),
      employeeId: e.employee_id ?? '',
      joinDate: e.hire_date ?? '',
      profilePictureUrl: e.profile_picture_url ?? '',
      status: e.status ?? 'Active',
    };
    // ⚠️ A background refresh must not overwrite what someone is typing.
    // Stale-while-revalidate paints the cache and then the server answers a
    // moment later; applying that second write unconditionally destroyed any
    // edit made in between — silently, because it looks like a load — and
    // reset the dirty baseline with it, so the guard then saw a clean form
    // and would have let them navigate away from work already lost.
    if (mayApplyRefresh(baselineRef.current, JSON.stringify(formDataRef.current))) {
      baselineRef.current = JSON.stringify(loaded);
      setFormData(loaded);
    }
    setAddresses(Array.isArray(e.addresses) ? e.addresses : []);
    setEmergencyContacts(Array.isArray(e.emergency_contacts) ? e.emergency_contacts : []);
    setEducationRecords(Array.isArray(e.education_records) ? e.education_records : []);
    setDocuments(Array.isArray(e.documents) ? e.documents : []);
  };

  useEffect(() => {
    // Stale-while-revalidate: paint the last-known profile instantly on reload,
    // then refresh in the background.
    const cached = getCachedProfile();
    if (cached) { applyProfile(cached); setLoading(false); }

    (async () => {
      try {
        const e = await getMyProfile();
        applyProfile(e);
        // Resolve the department name if the relation isn't embedded.
        if (e.department_id && !e.department?.name) {
          try {
            const depts = await getDepartments();
            const match = (depts.items || []).find((d: any) => d.id === e.department_id);
            if (match?.name) setFormData((d) => ({ ...d, department: match.name }));
          } catch { /* keep fallback */ }
        }
      } catch (err: any) {
        // Only surface an error if we had nothing cached to show.
        if (!cached) {
          if (err?.response?.status === 401 || err?.response?.status === 403 || err?.isSessionExpired) {
            setError(t('errors.session_expired'));
          } else {
            setError(errMsg(err, t('errors.load_failed')));
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (key: keyof typeof formData, value: string | boolean) =>
    setFormData((d) => ({ ...d, [key]: value }));

  // HR/owner (employee:write) may maintain statutory identifiers — even their own.
  // Plain employees see them read-only ("Managed by HR").
  const isHrEditor = useHasPermission('employee:write');
  // Compliance program: EVERYONE may edit identification. For non-HR the
  // server converts the edit into a pending change request (HTTP 202) — the
  // trusted value stays until HR approves.
  const canEditIdentification = true;
  const [pendingIdFields, setPendingIdFields] = useState<Record<string, string>>({});
  // {field: proposed value} across the person's open change requests.
  const loadPendingFields = async (): Promise<Record<string, string>> => {
    const reqs = await getMyChangeRequests().catch(() => []);
    const pending: Record<string, string> = {};
    for (const r of reqs) {
      if (r.status !== 'pending') continue;
      for (const [field, pair] of Object.entries<any>(r.changes || {})) {
        pending[field] = pair?.new ?? '';
      }
    }
    return pending;
  };
  useEffect(() => { loadPendingFields().then(setPendingIdFields).catch(() => undefined); }, []);
  // A name change can be policed too — compose the proposed name from whichever
  // parts are waiting, filling the rest from the approved record.
  const pendingLabel = t('id_pending_value', { defaultValue: 'Pending HR approval' });
  const pendingName = ['first_name', 'middle_name', 'last_name'].some((f) => f in pendingIdFields)
    ? [pendingIdFields.first_name ?? formData.firstName, pendingIdFields.middle_name ?? formData.middleName,
       pendingIdFields.last_name ?? formData.lastName].filter(Boolean).join(' ').trim()
    : '';

  // Always coerce an API error to a readable string. FastAPI 422s return
  // `detail` as an ARRAY of objects ({type,loc,msg,...}) — rendering that
  // directly crashes React, so flatten to the human messages.
  const errMsg = (err: any, fallback: string): string => {
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) {
      return d.map((x: any) => (x?.msg ? `${x.msg}${x?.loc ? ` (${x.loc[x.loc.length - 1]})` : ''}` : String(x))).join('; ');
    }
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
    return err?.message || fallback;
  };

  const reloadDocuments = async () => {
    if (!empId) return;
    try { setDocuments(await getDocuments(empId)); } catch { /* keep current */ }
  };

  // ---- Self-service sub-resource add / edit / delete (employee's own data) ----
  const closeChildForm = () => { setAddOpen(null); setEditId(null); };

  const submitAddress = async () => {
    if (!empId) return;
    if (!newAddress.street || !newAddress.city || !newAddress.region || !newAddress.postal_code) {
      setError(t('addr.required_error')); return;
    }
    setSavingChild(true); setError(null);
    try {
      if (editId) await updateAddress(empId, editId, newAddress);
      else await addAddress(empId, { ...newAddress, is_primary: addresses.length === 0 });
      setAddresses(await getAddresses(empId).catch(() => addresses));
      setNewAddress({ ...blankAddress }); closeChildForm();
    } catch (err: any) {
      setError(errMsg(err, t('addr.save_error')));
    } finally { setSavingChild(false); }
  };

  const submitEmergency = async () => {
    if (!empId) return;
    // Full name is composed from the structured parts; require first + last.
    const composedName = [newEmergency.first_name, newEmergency.middle_name, newEmergency.last_name]
      .map((s) => (s || '').trim()).filter(Boolean).join(' ');
    if (!newEmergency.first_name?.trim() || !newEmergency.last_name?.trim() || !newEmergency.relationship || !newEmergency.phone) {
      setError(t('emergency_form.required_error')); return;
    }
    if (!isValidPhone(newEmergency.phone)) {
      setError(t('emergency_form.invalid_phone', { defaultValue: PHONE_HINT })); return;
    }
    if (newEmergency.alternative_phone?.trim() && !isValidPhone(newEmergency.alternative_phone)) {
      setError(t('emergency_form.invalid_alt_phone', { defaultValue: PHONE_HINT })); return;
    }
    if (newEmergency.email?.trim() && !EMAIL_RE.test(newEmergency.email.trim())) {
      setError(t('emergency_form.invalid_email', { defaultValue: 'Enter a valid email address.' })); return;
    }
    setSavingChild(true); setError(null);
    try {
      const payload = {
        ...newEmergency,
        name: composedName,
        first_name: newEmergency.first_name?.trim() || null,
        middle_name: newEmergency.middle_name?.trim() || null,
        last_name: newEmergency.last_name?.trim() || null,
        preferred_name: newEmergency.preferred_name?.trim() || null,
        email: newEmergency.email || null,
        alternative_phone: newEmergency.alternative_phone || null,
      };
      if (editId) await updateEmergencyContact(empId, editId, payload);
      else await addEmergencyContact(empId, { ...payload, is_primary: emergencyContacts.length === 0 });
      setEmergencyContacts(await getEmergencyContacts(empId).catch(() => emergencyContacts));
      setNewEmergency({ ...blankEmergency }); closeChildForm();
    } catch (err: any) {
      setError(errMsg(err, t('emergency_form.save_error')));
    } finally { setSavingChild(false); }
  };

  const submitEducation = async () => {
    if (!empId) return;
    const year = parseInt(newEducation.year, 10);
    if (!newEducation.institution || !newEducation.degree || !newEducation.field || !year) {
      setError(t('education_form.required_error')); return;
    }
    setSavingChild(true); setError(null);
    try {
      const payload = { institution: newEducation.institution, degree: newEducation.degree, field: newEducation.field, year };
      if (editId) await updateEducation(empId, editId, payload);
      else await addEducation(empId, payload);
      setEducationRecords(await getEducation(empId).catch(() => educationRecords));
      setNewEducation({ ...blankEducation }); closeChildForm();
    } catch (err: any) {
      setError(errMsg(err, t('education_form.save_error')));
    } finally { setSavingChild(false); }
  };

  // Open a form pre-filled to edit an existing record.
  const editAddress = (a: any) => {
    setNewAddress({ street: a.street || '', city: a.city || '', region: a.region || '', postal_code: a.postal_code || '', district: a.district || '', ward: a.ward || '', country: a.country || 'Tanzania' });
    setEditId(a.id); setAddOpen('address');
  };
  const editEmergency = (c: any) => {
    const full = c.name || c.full_name || '';
    // Backfill structured parts from the stored full name for contacts created
    // before those fields existed (first token = first, last token = last).
    let first = c.first_name || '', middle = c.middle_name || '', last = c.last_name || '';
    if (!first && !last && full) {
      const parts = full.trim().split(/\s+/).filter(Boolean);
      first = parts[0] || '';
      last = parts.length > 1 ? parts[parts.length - 1] : '';
      middle = parts.length > 2 ? parts.slice(1, -1).join(' ') : '';
    }
    setNewEmergency({
      name: full, first_name: first, middle_name: middle, last_name: last, preferred_name: c.preferred_name || '',
      relationship: c.relationship || '', phone: c.phone || '', alternative_phone: c.alternative_phone || '', email: c.email || '',
    });
    setEditId(c.id); setAddOpen('emergency');
  };
  const editEducation = (ed: any) => {
    setNewEducation({ institution: ed.institution || '', degree: ed.degree || '', field: ed.field || ed.field_of_study || '', year: String(ed.year || ed.graduation_year || '') });
    setEditId(ed.id); setAddOpen('education');
  };

  // ---- Payout methods (bank / mobile money) — submitted for HR verification ----
  const openPaymentForm = (type: 'BANK' | 'MOBILE_MONEY') => {
    // Toggle closed if the same add-form is already open.
    if (addOpen === 'payment' && !editId && newPayment.type === type) { closeChildForm(); return; }
    setNewPayment({ ...blankPayment, type });
    setEditId(null);
    setAddOpen('payment');
  };

  const editPayment = (m: any) => {
    setNewPayment({
      type: m.type,
      bank_name: m.bank_name || '',
      branch: m.branch || '',
      account_holder_name: m.account_holder_name || '',
      account_number: '', // never returned — blank means keep the existing number
      mobile_money_provider: m.mobile_money_provider || 'M-Pesa',
      phone_number: '',
      country: m.country || 'Tanzania',
      currency: m.currency || 'TZS',
      is_primary: !!m.is_primary,
    });
    setEditId(m.id); setAddOpen('payment');
  };

  const submitPayment = async () => {
    const p = newPayment;
    if (p.type === 'BANK') {
      if (!p.bank_name.trim() || !p.account_holder_name.trim() || (!editId && !p.account_number.trim())) {
        setError(t('payment_form.bank_required', { defaultValue: 'Bank name, account holder and account number are required.' })); return;
      }
    } else {
      if (!p.mobile_money_provider || !p.account_holder_name.trim() || (!editId && !p.phone_number.trim())) {
        setError(t('payment_form.mobile_required', { defaultValue: 'Provider, account holder and phone number are required.' })); return;
      }
      if (p.phone_number.trim() && !isValidPhone(p.phone_number)) {
        setError(t('payment_form.invalid_phone', { defaultValue: PHONE_HINT })); return;
      }
    }
    setError(null);
    try {
      const payload: any = p.type === 'BANK'
        ? {
            type: 'BANK',
            bank_name: p.bank_name.trim(),
            branch: p.branch.trim() || undefined,
            account_holder_name: p.account_holder_name.trim(),
            country: p.country.trim() || undefined,
            currency: p.currency.trim() || undefined,
            is_primary: p.is_primary,
            ...(p.account_number.trim() ? { account_number: p.account_number.trim() } : {}),
          }
        : {
            type: 'MOBILE_MONEY',
            mobile_money_provider: p.mobile_money_provider,
            account_holder_name: p.account_holder_name.trim(),
            country: p.country.trim() || undefined,
            is_primary: p.is_primary,
            ...(p.phone_number.trim() ? { phone_number: p.phone_number.trim() } : {}),
          };
      if (editId) await updatePayment.mutateAsync({ id: editId, data: payload });
      else await addPayment.mutateAsync(payload);
      toast.success(t('payment_form.submitted', { defaultValue: 'Submitted for verification' }));
      setNewPayment({ ...blankPayment }); closeChildForm();
    } catch (err: any) {
      setError(errMsg(err, t('payment_form.save_error', { defaultValue: 'Could not save the payout method.' })));
    }
  };

  const setPaymentPrimary = async (id: string) => {
    setError(null);
    try {
      await setPrimaryPayment.mutateAsync(id);
      toast.success(t('payment.primary_set', { defaultValue: 'Primary payout method updated.' }));
    } catch (err: any) {
      setError(errMsg(err, t('payment.primary_error', { defaultValue: 'Could not set the primary method.' })));
    }
  };

  // Make an approved method THE destination payroll pays. A branded confirm,
  // because this moves where the next salary goes.
  const setPaymentDefault = async (m: { id: string; type: string; bank_name?: string; mobile_money_provider?: string; account_masked?: string; phone_masked?: string }) => {
    const label = m.type === 'BANK'
      ? [m.bank_name, m.account_masked].filter(Boolean).join(' ')
      : [m.mobile_money_provider, m.phone_masked].filter(Boolean).join(' ');
    const ok = await confirm({
      title: t('payment.default_confirm_title', { defaultValue: 'Pay your salary here?' }),
      message: t('payment.default_confirm_body', {
        defaultValue: 'From the next payroll run your salary goes to {{label}}. Runs already locked are not affected.',
        label,
      }),
      confirmLabel: t('payment.default_confirm_cta', { defaultValue: 'Set as default' }),
    });
    if (!ok) return;
    setError(null);
    try {
      await setDefaultPayment.mutateAsync(m.id);
      toast.success(t('payment.default_set', { defaultValue: 'Salary will now be paid to this method.' }));
    } catch (err: any) {
      setError(errMsg(err, t('payment.default_error', { defaultValue: 'Could not change your default payment method.' })));
    }
  };

  // Open the styled confirm modal (replaces the native window.confirm).
  const removeChild = (kind: 'address' | 'emergency' | 'education' | 'payment', id: string) => setConfirmRemove({ kind, id });

  const doRemove = async () => {
    const c = confirmRemove;
    if (!c) return;
    if (c.kind !== 'payment' && !empId) return;
    setConfirmRemove(null);
    setError(null);
    try {
      if (c.kind === 'address') { await deleteAddress(empId!, c.id); setAddresses(await getAddresses(empId!).catch(() => addresses)); }
      else if (c.kind === 'emergency') { await deleteEmergencyContact(empId!, c.id); setEmergencyContacts(await getEmergencyContacts(empId!).catch(() => emergencyContacts)); }
      else if (c.kind === 'payment') { await deactivatePayment.mutateAsync(c.id); }
      else { await deleteEducation(empId!, c.id); setEducationRecords(await getEducation(empId!).catch(() => educationRecords)); }
    } catch (err: any) {
      setError(errMsg(err, t('remove.error')));
    }
  };

  // Small helpers for the add-forms.
  const childInput = "w-full px-4 py-2 rounded-lg border border-border focus:border-orange-500 focus:ring-2 focus:ring-orange-200";

  const handlePickDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !empId) return;
    setSaving(true);
    setError(null);
    try {
      await uploadDocument(empId, file, 'other');
      await reloadDocuments();
    } catch (err: any) {
      setError(errMsg(err, t('documents.upload_error')));
    } finally {
      setSaving(false);
    }
  };

  // Pick an image -> open the crop/preview modal; the cropper returns the final
  // square Blob, which we then upload to object storage.
  const handlePickAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('errors.choose_image'));
      return;
    }
    setError(null);
    setCropFile(file);
  };

  const handleCroppedUpload = async (blob: Blob) => {
    setCropFile(null);
    if (!empId) return;
    setSaving(true);
    setError(null);
    try {
      const { url } = await uploadProfilePicture(empId, blob);
      // Cache-bust so the new image shows immediately.
      const busted = `${url}?t=${Date.now()}`;
      setFormData((d) => ({ ...d, profilePictureUrl: busted }));
      // Reflect it in the header avatar (and anywhere reading user.avatar) live.
      dispatch(updateUser({ avatar: busted }));
    } catch (err: any) {
      setError(errMsg(err, t('errors.photo_error')));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!empId) {
      setIsEditing(false);
      return;
    }
    // Basic field validation before saving.
    const phone = (formData.phone || '').trim();
    if (phone && !isValidPhone(phone)) {
      setError(t('errors.invalid_phone', { defaultValue: 'Enter a valid phone number in international format, e.g. +255712345678.' }));
      return;
    }
    if (canEditIdentification) {
      if (!isValidNationalId(formData.nationalId || '')) { setError(NATIONAL_ID_HINT); return; }
      if (!isValidNSSF(formData.nssf || '')) { setError(NSSF_HINT); return; }
      if (!isValidTIN(formData.tin || '')) { setError(TIN_HINT); return; }
    }
    // Structured name: first + last are required, middle + preferred optional.
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError(t('errors.name_required', { defaultValue: 'First name and last name are required.' }));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Self-service edit: personal fields only. Employment fields (position,
      // department, hire date, employee ID, status, manager) stay admin/HR-managed.
      // Statutory ids (national_id, NSSF, TIN) are editable ONLY for employee:write
      // holders (HR/owner) — plain employees can't tamper with official data.
      const res: any = await updateEmployee(empId, {
        first_name: formData.firstName.trim(),
        middle_name: formData.middleName.trim() || null,
        last_name: formData.lastName.trim(),
        preferred_name: formData.preferredName.trim() || null,
        phone: formData.phone || null,
        dob: formData.dob || null,
        show_birthday: formData.showBirthday,
        nationality: formData.nationality || null,
        marital_status: formData.maritalStatus || null,
        driver_license: formData.driverLicense || null,
        national_id: formData.nationalId || null,
        nssf: formData.nssf || null,
        tin: formData.tin || null,
      });
      if (res?.status === 'pending_approval') {
        // The server gates the WHOLE edit, so anything else typed in the same
        // save (phone, nationality, …) was not applied either — say so.
        const gated: string[] = Array.isArray(res?.fields) ? res.fields : [];
        toast.success(gated.length
          ? t('id_pending_toast_fields', {
            defaultValue: 'Waiting on HR: {{fields}}. Your approved details stay in place, and nothing else in this save was applied — make other edits in a separate save.',
            fields: gated.map(fieldLabel).join(', '),
          })
          : t('id_pending_toast', {
            defaultValue: 'Submitted for HR approval — your current approved details stay in place until then.' }),
          { duration: 8000 });
        // Nothing in this PUT was applied (the server gates the whole edit), so
        // re-read the record: keeping the typed values on screen would look
        // saved until the next refresh dropped them.
        const fresh = await getMyProfile().catch(() => null);
        if (fresh) applyProfile(fresh);
        setPendingIdFields(await loadPendingFields());
      }
      setIsEditing(false);
    } catch (err: any) {
      setError(errMsg(err, t('errors.save_error')));
    } finally {
      setSaving(false);
    }
  };

  // Avatar initials from the loaded name.
  const initials = (displayFullName || "U")
    .split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  // Employment-status colour: Active=green, OnLeave=amber, Inactive=gray, Terminated=red.
  const statusUi = (() => {
    switch ((formData.status || '').toLowerCase()) {
      case 'onleave': return { text: 'text-warning', bg: 'bg-warning-soft' };
      case 'inactive': return { text: 'text-muted-foreground', bg: 'bg-muted' };
      case 'terminated': return { text: 'text-danger', bg: 'bg-danger-soft' };
      default: return { text: 'text-success', bg: 'bg-success-soft' };
    }
  })();

  return (
    <div className="space-y-6 relative">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-transparent to-amber-50/30 pointer-events-none dark:opacity-0" />

      {cropFile && (
        <AvatarCropper file={cropFile} onCancel={() => setCropFile(null)} onConfirm={handleCroppedUpload} />
      )}

      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmRemove(null)}>
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-danger-soft flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-danger" />
              </div>
              <div>
                {confirmRemove.kind === 'payment' ? (
                  <>
                    {/* PM11: a payout method is DEACTIVATED (kept as history), and salary is never redirected. */}
                    <h3 className="text-lg font-semibold text-foreground">{t('payment.deactivate_title', { defaultValue: 'Deactivate this payout method?' })}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{t('payment.deactivate_warning', { defaultValue: 'It will no longer receive salary and stays in your history. If payroll pays you here, your salary is held until you choose a new default — it is never sent to your other account automatically.' })}</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-foreground">{t('remove.title', { kind: t(`remove.kind_${confirmRemove.kind === 'emergency' ? 'contact' : confirmRemove.kind}`, { defaultValue: confirmRemove.kind }) })}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{t('remove.warning')}</p>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setConfirmRemove(null)}
                className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted text-sm font-medium">
                {t('remove.cancel')}
              </button>
              <button type="button" onClick={doRemove}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm font-medium">
                {confirmRemove.kind === 'payment' ? t('payment.deactivate', { defaultValue: 'Deactivate' }) : t('remove.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="relative z-10 flex items-start gap-3 p-4 rounded-xl bg-danger-soft border border-danger text-sm text-danger-strong">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      
      {/* Clean Profile Header */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {/* Avatar — click to upload a photo */}
            <div className="relative">
              <label
                className={`block w-20 h-20 rounded-xl overflow-hidden bg-gradient-to-br from-orange-100 dark:from-orange-500/[0.12] to-amber-100 dark:to-amber-500/[0.08] flex items-center justify-center ${isEditing ? 'cursor-pointer group' : ''}`}
                title={isEditing ? t('change_photo') : undefined}
              >
                {formData.profilePictureUrl ? (
                  <img src={formData.profilePictureUrl} alt={displayFullName || "Profile"} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-orange-600">{initials}</span>
                )}
                {isEditing && (
                  <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-medium">
                    {saving ? '…' : t('change')}
                  </span>
                )}
                {isEditing && (
                  <input type="file" accept="image/*" className="hidden" onChange={handlePickAvatar} aria-label="Upload profile picture" />
                )}
              </label>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            </div>
            
            {/* Info */}
            <div className="min-w-0">
              <h1 className="text-h2 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent truncate">{greetingName}</h1>
              <p className="text-sm text-muted-foreground mt-1 truncate">{formData.jobTitle} • {formData.department}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1 min-w-0">
                  <Mail className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{formData.email}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" />
                  {formData.phone}
                </span>
                <span className="flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" />
                  {formData.nationality}
                </span>
              </div>
            </div>
          </div>
          
          {/* Edit Button */}
          <button
            onClick={() => isEditing ? handleSave() : setIsEditing(true)}
            className={`shrink-0 self-start sm:self-auto px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
              isEditing
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-lg'
                : 'bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:shadow-lg hover:scale-105'
            }`}
            type="button"
          >
            {isEditing ? (
              <>
                <Save className="w-4 h-4" />
                {t('save')}
              </>
            ) : (
              <>
                <Edit className="w-4 h-4" />
                {t('edit_profile')}
              </>
            )}
          </button>
        </div>
        
        {/* Simple Progress Bar */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">{t('profile_completion')}</span>
            <span className="text-lg font-bold text-orange-600">{profileCompletion}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-400 to-amber-400 rounded-full transition-all duration-500"
              style={{ width: `${profileCompletion}%` }}
            />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 shadow-sm border border-border hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('employee_id')}</p>
              <p className="text-lg font-bold text-foreground mt-1">{formData.employeeId}</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-500/[0.12] rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl p-4 shadow-sm border border-border hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('join_date')}</p>
              <p className="text-lg font-bold text-foreground mt-1">{formData.joinDate}</p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl p-4 shadow-sm border border-border hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('documents_label')}</p>
              <p className="text-lg font-bold text-foreground mt-1">{documents.length} {t('files')}</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-500/[0.12] rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl p-4 shadow-sm border border-border hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('status')}</p>
              <p className={`text-lg font-bold mt-1 ${statusUi.text}`}>{formData.status || t('active')}</p>
            </div>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusUi.bg}`}>
              <Check className={`w-5 h-5 ${statusUi.text}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-card rounded-2xl shadow-lg border border-border overflow-hidden">
        <div className="border-b border-border bg-gradient-to-r from-muted to-muted/50">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group flex-1 min-w-[140px] px-6 py-4 flex flex-col items-center gap-2 border-b-[3px] transition-all duration-300 ${
                    isActive 
                      ? 'bg-card border-orange-500 shadow-sm' 
                      : 'border-transparent hover:bg-card/60'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    isActive 
                      ? `bg-gradient-to-br ${tab.bgGradient} shadow-lg group-hover:scale-110` 
                      : 'bg-muted group-hover:bg-muted'
                  }`}>
                    <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-muted-foreground'}`} />
                  </div>
                  <span className={`text-sm font-medium transition-colors ${
                    isActive ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-8">
          {activeTab === 'basic' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-xl p-6 border border-orange-100 dark:border-orange-500/20">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{t('basic_info.title')}</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  {isEditing ? (
                    <>
                      {/* Edit mode: capture the structured name parts. */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                          { key: 'firstName', label: t('basic_info.first_name', { defaultValue: 'First Name' }), required: true },
                          { key: 'middleName', label: t('basic_info.middle_name', { defaultValue: 'Middle Name' }), required: false },
                          { key: 'lastName', label: t('basic_info.last_name', { defaultValue: 'Last Name' }), required: true },
                          { key: 'preferredName', label: t('basic_info.preferred_name', { defaultValue: 'Preferred Name' }), required: false },
                        ].map((f) => (
                          <div key={f.key}>
                            <label htmlFor={f.key} className="block text-sm font-medium text-foreground mb-2">
                              {f.label}{' '}
                              {f.required
                                ? <span className="text-red-500">*</span>
                                : <span className="text-muted-foreground text-xs">({t('basic_info.optional', { defaultValue: 'optional' })})</span>}
                            </label>
                            <input
                              id={f.key}
                              type="text"
                              value={(formData as any)[f.key]}
                              onChange={(e) => set(f.key as any, e.target.value)}
                              aria-label={f.label}
                              className="w-full px-4 py-3 rounded-lg border border-border focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                            />
                          </div>
                        ))}
                      </div>
                      {displayFullName && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          {t('basic_info.displays_as', { defaultValue: 'Displays as' })}{' '}
                          <span className="font-medium text-foreground">{displayFullName}</span>
                          {formData.preferredName?.trim() && (
                            <> · {t('basic_info.goes_by', { defaultValue: 'goes by' })} <span className="font-medium text-foreground">{formData.preferredName.trim()}</span></>
                          )}
                        </p>
                      )}
                    </>
                  ) : (
                    /* View mode: single composed Full Name. */
                    <div>
                      <ReadField label={t('basic_info.full_name', { defaultValue: 'Full Name' })} value={displayFullName} />
                      {formData.preferredName?.trim() && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          {t('basic_info.goes_by', { defaultValue: 'goes by' })}{' '}
                          <span className="font-medium text-foreground">{formData.preferredName.trim()}</span>
                        </p>
                      )}
                      <PendingValue label={pendingLabel} value={pendingName} />
                    </div>
                  )}
                </div>
                
                <div>
                  {isEditing ? (
                    <>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                        {t('basic_info.email')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={formData.email}
                        disabled={!isEditing}
                        aria-label="Email"
                        className="w-full px-4 py-3 rounded-lg border border-border focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors disabled:bg-muted disabled:text-muted-foreground"
                      />
                    </>
                  ) : (
                    <ReadField label={t('basic_info.email')} value={formData.email} />
                  )}
                  <PendingValue label={pendingLabel} value={pendingIdFields.email || ''} />
                </div>

                <div>
                  {isEditing ? (
                    <>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                        {t('basic_info.phone')} <span className="text-red-500">*</span>
                      </label>
                      <PhoneInput
                        id="phone"
                        value={formData.phone}
                        onChange={(v) => set('phone', v)}
                        disabled={!isEditing}
                      />
                      <p className="text-xs text-muted-foreground mt-1">{t('basic_info.phone_format', { defaultValue: 'Pick your country, then enter your number.' })}</p>
                    </>
                  ) : (
                    <ReadField label={t('basic_info.phone')} value={formData.phone} />
                  )}
                </div>

                <div>
                  {isEditing ? (
                    <>
                      <label htmlFor="dob" className="block text-sm font-medium text-foreground mb-2">
                        {t('basic_info.date_of_birth')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="dob"
                        type="date"
                        max={new Date().toISOString().slice(0, 10)}
                        value={formData.dob}
                        onChange={(e) => set('dob', e.target.value)}
                        disabled={!isEditing}
                        aria-label="Date of Birth"
                        className="w-full px-4 py-3 rounded-lg border border-border focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors disabled:bg-muted disabled:text-muted-foreground"
                      />
                    </>
                  ) : (
                    <ReadField
                      label={t('basic_info.date_of_birth')}
                      value={formData.dob ? toDisplayDate(formData.dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    />
                  )}
                  <PendingValue label={pendingLabel} value={pendingIdFields.dob || ''} />
                  {/* Birthday opt-in: personal, off by default. Anniversaries need no opt-in. */}
                  <label className={`mt-2 flex items-start gap-2 text-sm ${isEditing ? 'cursor-pointer' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.showBirthday}
                      onChange={(e) => set('showBirthday', e.target.checked)}
                      disabled={!isEditing}
                      className="mt-0.5 h-4 w-4 rounded border-border text-orange-600 focus:ring-orange-200 disabled:opacity-60"
                    />
                    <span className="text-muted-foreground">
                      {t('basic_info.show_birthday', 'Show my birthday in the team celebrations feed')}
                    </span>
                  </label>
                </div>

                <div>
                  {isEditing ? (
                    <>
                      <label htmlFor="nationality" className="block text-sm font-medium text-foreground mb-2">{t('basic_info.nationality')}</label>
                      <input
                        id="nationality"
                        type="text"
                        value={formData.nationality}
                        onChange={(e) => set('nationality', e.target.value)}
                        disabled={!isEditing}
                        aria-label="Nationality"
                        className="w-full px-4 py-3 rounded-lg border border-border focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors disabled:bg-muted disabled:text-muted-foreground"
                      />
                    </>
                  ) : (
                    <ReadField label={t('basic_info.nationality')} value={formData.nationality} />
                  )}
                </div>

                <div>
                  {isEditing ? (
                    <>
                      <label htmlFor="maritalStatus" className="block text-sm font-medium text-foreground mb-2">{t('basic_info.marital_status')}</label>
                      <select
                        id="maritalStatus"
                        value={formData.maritalStatus}
                        onChange={(e) => set('maritalStatus', e.target.value)}
                        disabled={!isEditing}
                        aria-label="Marital Status"
                        className="w-full px-4 py-3 rounded-lg border border-border focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors disabled:bg-muted disabled:text-muted-foreground"
                      >
                        <option value="Single">{t('basic_info.single')}</option>
                        <option value="Married">{t('basic_info.married')}</option>
                        <option value="Divorced">{t('basic_info.divorced')}</option>
                        <option value="Widowed">{t('basic_info.widowed')}</option>
                      </select>
                    </>
                  ) : (
                    <ReadField label={t('basic_info.marital_status')} value={formData.maritalStatus} />
                  )}
                </div>
              </div>
              </div>

              <div className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-xl p-6 border border-orange-100 dark:border-orange-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-foreground">{t('basic_info.identification')}</h3>
                    {['national_id', 'nssf', 'tin', 'driver_license'].some((f) => f in pendingIdFields) && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-500/[0.15] text-orange-700">
                        {t('id_pending_chip', { defaultValue: 'Pending HR approval' })}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  {isHrEditor
                    ? t('managed_by_hr_note_admin', { defaultValue: 'As an administrator you can maintain these statutory identifiers.' })
                    : t('id_self_note', { defaultValue: 'You can submit your own identification — changes take effect after HR approves them.' })}
                </p>
                {(() => {
                  const idEditable = canEditIdentification && isEditing;
                  const idCls = idEditable
                    ? 'w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors'
                    : 'w-full px-4 py-3 rounded-lg border border-border bg-muted text-muted-foreground';
                  return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    {idEditable ? (
                      <>
                        <label htmlFor="nationalId" className="block text-sm font-medium text-foreground mb-2">
                          {t('basic_info.national_id')}
                        </label>
                        <input
                          id="nationalId"
                          type="text"
                          value={formData.nationalId}
                          onChange={(e) => set('nationalId', e.target.value)}
                          readOnly={!idEditable}
                          disabled={!idEditable}
                          aria-label="National ID Number"
                          className={idCls}
                        />
                      </>
                    ) : (
                      <ReadField label={t('basic_info.national_id')} value={formData.nationalId} />
                    )}
                    <PendingValue label={pendingLabel} value={pendingIdFields.national_id || ''} />
                  </div>

                  <div>
                    {idEditable ? (
                      <>
                        <label htmlFor="nssf" className="block text-sm font-medium text-foreground mb-2">
                          {t('basic_info.nssf_number')}
                        </label>
                        <input
                          id="nssf"
                          type="text"
                          value={formData.nssf}
                          onChange={(e) => set('nssf', e.target.value)}
                          readOnly={!idEditable}
                          disabled={!idEditable}
                          aria-label="NSSF Number"
                          className={idCls}
                        />
                      </>
                    ) : (
                      <ReadField label={t('basic_info.nssf_number')} value={formData.nssf} />
                    )}
                    <PendingValue label={pendingLabel} value={pendingIdFields.nssf || ''} />
                  </div>

                  <div>
                    {idEditable ? (
                      <>
                        <label htmlFor="tin" className="block text-sm font-medium text-foreground mb-2">
                          {t('basic_info.tin_number', { defaultValue: 'TIN (Tax Identification Number)' })}
                        </label>
                        <input
                          id="tin"
                          type="text"
                          value={formData.tin}
                          onChange={(e) => set('tin', e.target.value)}
                          readOnly={!idEditable}
                          disabled={!idEditable}
                          aria-label="TIN (Tax Identification Number)"
                          className={idCls}
                        />
                      </>
                    ) : (
                      <ReadField label={t('basic_info.tin_number', { defaultValue: 'TIN (Tax Identification Number)' })} value={formData.tin} />
                    )}
                    <PendingValue label={pendingLabel} value={pendingIdFields.tin || ''} />
                  </div>
                </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Address Tab */}
          {activeTab === 'address' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{t('address.title')}</h2>
                <button type="button" onClick={() => { if (addOpen === 'address') { closeChildForm(); } else { setNewAddress({ ...blankAddress }); setEditId(null); setAddOpen('address'); } }}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:shadow-lg text-sm font-medium">
                  {addOpen === 'address' ? t('actions.cancel') : t('addr.add')}
                </button>
              </div>

              {addOpen === 'address' && (
                <div className="bg-card rounded-xl p-5 border border-orange-200 dark:border-orange-500/25 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input className={childInput} placeholder={t('addr.street')} value={newAddress.street} onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })} />
                    <input className={childInput} placeholder={t('addr.city')} value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })} />
                    <select className={childInput} aria-label="Region" value={newAddress.region} onChange={(e) => setNewAddress({ ...newAddress, region: e.target.value })}>
                      <option value="">{t('addr.region')}</option>
                      {TZ_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input className={childInput} placeholder={t('addr.postal_code')} value={newAddress.postal_code} onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })} />
                    <input className={childInput} placeholder={t('addr.district')} value={newAddress.district} onChange={(e) => setNewAddress({ ...newAddress, district: e.target.value })} />
                    <input className={childInput} placeholder={t('addr.ward')} value={newAddress.ward} onChange={(e) => setNewAddress({ ...newAddress, ward: e.target.value })} />
                  </div>
                  <button type="button" disabled={savingChild} onClick={submitAddress}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    {savingChild ? t('saving') : editId ? t('addr.update') : t('addr.save')}
                  </button>
                </div>
              )}

              {addresses.length === 0 ? (
                <div className="bg-muted rounded-xl p-10 border border-dashed border-border text-center text-muted-foreground">
                  <MapPin className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm">{t('address.none', { defaultValue: 'No address on file yet.' })}</p>
                </div>
              ) : (
                addresses.map((addr, i) => (
                  <div key={addr.id ?? i} className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-xl p-6 border border-orange-100 dark:border-orange-500/20">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{addr.address_type || t('address.primary_address')}</h3>
                          <p className="text-sm text-muted-foreground">{t('address.home_address')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(addr.is_primary || i === 0) && (
                          <span className="px-3 py-1 bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 rounded-full text-xs font-medium">{t('primary')}</span>
                        )}
                        <button type="button" onClick={() => editAddress(addr)} className="text-xs text-orange-700 hover:underline">{t('actions.edit')}</button>
                        <button type="button" onClick={() => removeChild('address', addr.id)} className="text-xs text-red-600 hover:underline">{t('actions.remove')}</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ReadField label={t('address.street_address')} value={addr.street || addr.address_line1 || ''} />
                      <ReadField label={t('address.city')} value={addr.city || ''} />
                      <ReadField label={t('address.region_state')} value={addr.region || addr.state || ''} />
                      <ReadField label={t('address.postal_code')} value={addr.postal_code || ''} />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Emergency Contact Tab */}
          {activeTab === 'emergency' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{t('emergency.title')}</h2>
                <button type="button" onClick={() => { if (addOpen === 'emergency') { closeChildForm(); } else { setNewEmergency({ ...blankEmergency }); setEditId(null); setAddOpen('emergency'); } }}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:shadow-lg text-sm font-medium">
                  {addOpen === 'emergency' ? t('actions.cancel') : t('emergency_form.add')}
                </button>
              </div>

              {addOpen === 'emergency' && (
                <div className="bg-card rounded-xl p-5 border border-orange-200 dark:border-orange-500/25 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input className={childInput} placeholder={t('emergency_form.first_name', { defaultValue: 'First name' })} value={newEmergency.first_name} onChange={(e) => setNewEmergency({ ...newEmergency, first_name: e.target.value })} />
                    <input className={childInput} placeholder={t('emergency_form.last_name', { defaultValue: 'Last name' })} value={newEmergency.last_name} onChange={(e) => setNewEmergency({ ...newEmergency, last_name: e.target.value })} />
                    <input className={childInput} placeholder={t('emergency_form.middle_name', { defaultValue: 'Middle name (optional)' })} value={newEmergency.middle_name} onChange={(e) => setNewEmergency({ ...newEmergency, middle_name: e.target.value })} />
                    <input className={childInput} placeholder={t('emergency_form.preferred_name', { defaultValue: 'Preferred name (optional)' })} value={newEmergency.preferred_name} onChange={(e) => setNewEmergency({ ...newEmergency, preferred_name: e.target.value })} />
                    <input className={childInput} placeholder={t('emergency_form.relationship')} value={newEmergency.relationship} onChange={(e) => setNewEmergency({ ...newEmergency, relationship: e.target.value })} />
                    <PhoneInput value={newEmergency.phone} onChange={(v) => setNewEmergency({ ...newEmergency, phone: v })} />
                    <input className={childInput} placeholder={t('emergency_form.email')} value={newEmergency.email} onChange={(e) => setNewEmergency({ ...newEmergency, email: e.target.value })} />
                  </div>
                  <button type="button" disabled={savingChild} onClick={submitEmergency}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    {savingChild ? t('saving') : editId ? t('emergency_form.update') : t('emergency_form.save')}
                  </button>
                </div>
              )}

              {emergencyContacts.length === 0 ? (
                <div className="bg-muted rounded-xl p-10 border border-dashed border-border text-center text-muted-foreground">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm">{t('emergency.none', { defaultValue: 'No emergency contact on file yet.' })}</p>
                </div>
              ) : (
                emergencyContacts.map((c, i) => (
                  <div key={c.id ?? i} className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-xl p-6 border border-orange-100 dark:border-orange-500/20">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{c.name || c.full_name || '—'}</h3>
                          <p className="text-sm text-muted-foreground">{c.relationship || ''}{(c.is_primary || i === 0) ? ` • ${t('emergency.primary_contact')}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => editEmergency(c)} className="text-xs text-orange-700 hover:underline">{t('actions.edit')}</button>
                        <button type="button" onClick={() => removeChild('emergency', c.id)} className="text-xs text-red-600 hover:underline">{t('actions.remove')}</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ReadField label={t('emergency.full_name')} value={c.name || c.full_name || ''} />
                      <ReadField label={t('emergency.relationship')} value={c.relationship || ''} />
                      <ReadField label={t('emergency.phone_number')} value={c.phone || c.phone_number || ''} />
                      <ReadField label={t('emergency.email_address')} value={c.email || ''} />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Payment Details Tab */}
          {activeTab === 'payment' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                <h2 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                  {t('payment.title', { defaultValue: 'Payment Details' })}
                </h2>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => openPaymentForm('BANK')}
                    className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:shadow-lg text-sm font-medium">
                    {t('payment.add_bank', { defaultValue: 'Add Bank Account' })}
                  </button>
                  <button type="button" onClick={() => openPaymentForm('MOBILE_MONEY')}
                    className="px-4 py-2 rounded-lg border border-orange-300 dark:border-orange-500/40 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-500/[0.08] text-sm font-medium">
                    {t('payment.add_mobile', { defaultValue: 'Add Mobile Money Wallet' })}
                  </button>
                </div>
              </div>

              {/* PM11: salary on hold after the destination was deactivated / changed */}
              <PayoutHoldBanner methods={paymentMethods} self />

              {/* No approved method warning (only when methods exist but none approved) */}
              {paymentMethods.length > 0 && !paymentMethods.some((m) => m.status === 'APPROVED') && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-warning-soft border border-warning text-sm text-warning-strong">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{t('payment.no_approved', { defaultValue: "You don't have an approved payout method yet — payroll can't pay you until HR verifies one." })}</span>
                </div>
              )}

              {/* Add / edit inline form */}
              {addOpen === 'payment' && (
                <div className="bg-card rounded-xl p-5 border border-orange-200 dark:border-orange-500/25 space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    {newPayment.type === 'BANK'
                      ? (editId ? t('payment_form.edit_bank', { defaultValue: 'Edit bank account' }) : t('payment_form.new_bank', { defaultValue: 'New bank account' }))
                      : (editId ? t('payment_form.edit_mobile', { defaultValue: 'Edit mobile money wallet' }) : t('payment_form.new_mobile', { defaultValue: 'New mobile money wallet' }))}
                  </p>
                  {newPayment.type === 'BANK' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input className={childInput} placeholder={t('payment_form.bank_name', { defaultValue: 'Bank name' })} value={newPayment.bank_name} onChange={(e) => setNewPayment({ ...newPayment, bank_name: e.target.value })} />
                      <input className={childInput} placeholder={t('payment_form.account_holder', { defaultValue: 'Account holder name' })} value={newPayment.account_holder_name} onChange={(e) => setNewPayment({ ...newPayment, account_holder_name: e.target.value })} />
                      <input className={childInput} placeholder={editId ? t('payment_form.account_number_keep', { defaultValue: 'Account number (leave blank to keep)' }) : t('payment_form.account_number', { defaultValue: 'Account number' })} value={newPayment.account_number} onChange={(e) => setNewPayment({ ...newPayment, account_number: e.target.value })} />
                      <input className={childInput} placeholder={t('payment_form.branch', { defaultValue: 'Branch (optional)' })} value={newPayment.branch} onChange={(e) => setNewPayment({ ...newPayment, branch: e.target.value })} />
                      <input className={childInput} placeholder={t('payment_form.country', { defaultValue: 'Country' })} value={newPayment.country} onChange={(e) => setNewPayment({ ...newPayment, country: e.target.value })} />
                      <input className={childInput} placeholder={t('payment_form.currency', { defaultValue: 'Currency' })} value={newPayment.currency} onChange={(e) => setNewPayment({ ...newPayment, currency: e.target.value })} />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <select className={childInput} aria-label={t('payment_form.provider', { defaultValue: 'Mobile money provider' })} value={newPayment.mobile_money_provider} onChange={(e) => setNewPayment({ ...newPayment, mobile_money_provider: e.target.value })}>
                        {MOBILE_MONEY_PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <PhoneInput value={newPayment.phone_number} onChange={(v) => setNewPayment({ ...newPayment, phone_number: v })} />
                      <input className={childInput} placeholder={t('payment_form.account_holder', { defaultValue: 'Account holder name' })} value={newPayment.account_holder_name} onChange={(e) => setNewPayment({ ...newPayment, account_holder_name: e.target.value })} />
                      <input className={childInput} placeholder={t('payment_form.country', { defaultValue: 'Country' })} value={newPayment.country} onChange={(e) => setNewPayment({ ...newPayment, country: e.target.value })} />
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input type="checkbox" checked={newPayment.is_primary} onChange={(e) => setNewPayment({ ...newPayment, is_primary: e.target.checked })} />
                    {newPayment.type === 'BANK'
                      ? t('payment_form.make_preferred_bank', { defaultValue: 'Make this my preferred bank account' })
                      : t('payment_form.make_preferred_wallet', { defaultValue: 'Make this my preferred wallet' })}
                  </label>
                  <div className="flex items-center gap-2">
                    <button type="button" disabled={savingPayment} onClick={submitPayment}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                      {savingPayment ? t('saving') : editId ? t('payment_form.update', { defaultValue: 'Update' }) : t('payment_form.submit', { defaultValue: 'Submit for verification' })}
                    </button>
                    <button type="button" onClick={closeChildForm}
                      className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted text-sm font-medium">
                      {t('actions.cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* Loading / error / empty / list — an error must never read as "no methods" */}
              {paymentMethodsQuery.isLoading ? (
                <div className="bg-muted rounded-xl p-10 border border-border text-center text-muted-foreground text-sm">
                  {t('payment.loading', { defaultValue: 'Loading payout methods…' })}
                </div>
              ) : paymentMethodsQuery.isError ? (
                <div className="bg-danger-soft text-danger-strong rounded-xl p-6 text-sm">
                  {errMsg(paymentMethodsQuery.error, t('payment.load_error', { defaultValue: 'Could not load your payout methods. Please refresh to try again.' }))}
                </div>
              ) : paymentMethods.length === 0 ? (
                <div className="bg-muted rounded-xl p-10 border border-dashed border-border text-center text-muted-foreground">
                  <Wallet className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm">{t('payment.none', { defaultValue: 'No payout method on file. Add a bank account or mobile money wallet so payroll can pay you.' })}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {paymentMethods.map((m) => (
                    <div key={m.id} className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-xl p-6 border border-orange-100 dark:border-orange-500/20">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Wallet className="w-5 h-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-foreground truncate">
                              {m.type === 'BANK'
                                ? [m.bank_name, m.account_masked].filter(Boolean).join(' ') || t('payment.bank', { defaultValue: 'Bank account' })
                                : [m.mobile_money_provider, m.phone_masked].filter(Boolean).join(' ') || t('payment.mobile', { defaultValue: 'Mobile money' })}
                            </h3>
                            <p className="text-sm text-muted-foreground truncate">{m.account_holder_name || ''}</p>
                          </div>
                        </div>
                        <PaymentMethodChips method={m} className="flex-shrink-0" />
                      </div>
                      {m.status === 'REJECTED' && m.rejection_reason && (
                        <p className="text-sm text-danger mb-3">
                          {t('payment.rejected_reason', { defaultValue: 'Rejected' })}: {m.rejection_reason}
                        </p>
                      )}
                      <div className="flex items-center gap-3">
                        {canDeactivate(m) && (
                          <button type="button" onClick={() => editPayment(m)} className="text-xs text-orange-700 dark:text-orange-300 hover:underline">{t('actions.edit')}</button>
                        )}
                        {canBeDefault(m) && (
                          <button type="button" disabled={setDefaultPayment.isPending} onClick={() => setPaymentDefault(m)} className="text-xs font-semibold text-primary-600 hover:underline disabled:opacity-50">{t('payment.set_default', { defaultValue: 'Set as default' })}</button>
                        )}
                        {m.status === 'APPROVED' && !m.is_primary && !m.is_default && (
                          <button type="button" onClick={() => setPaymentPrimary(m.id)} className="text-xs text-muted-foreground hover:underline">
                            {m.type === 'BANK'
                              ? t('payment.set_preferred_bank', { defaultValue: 'Prefer this bank' })
                              : t('payment.set_preferred_wallet', { defaultValue: 'Prefer this wallet' })}
                          </button>
                        )}
                        {canDeactivate(m) && (
                          <button type="button" onClick={() => removeChild('payment', m.id)} className="text-xs text-red-600 hover:underline">{t('payment.deactivate', { defaultValue: 'Deactivate' })}</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Education Tab */}
          {activeTab === 'education' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{t('education.title')}</h2>
                <button type="button" onClick={() => { if (addOpen === 'education') { closeChildForm(); } else { setNewEducation({ ...blankEducation }); setEditId(null); setAddOpen('education'); } }}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:shadow-lg text-sm font-medium">
                  {addOpen === 'education' ? t('actions.cancel') : t('education_form.add')}
                </button>
              </div>

              {addOpen === 'education' && (
                <div className="bg-card rounded-xl p-5 border border-orange-200 dark:border-orange-500/25 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input className={childInput} placeholder={t('education_form.institution')} value={newEducation.institution} onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })} />
                    <input className={childInput} placeholder={t('education_form.degree')} value={newEducation.degree} onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })} />
                    <input className={childInput} placeholder={t('education_form.field')} value={newEducation.field} onChange={(e) => setNewEducation({ ...newEducation, field: e.target.value })} />
                    <input className={childInput} type="number" placeholder={t('education_form.year')} value={newEducation.year} onChange={(e) => setNewEducation({ ...newEducation, year: e.target.value })} />
                  </div>
                  <button type="button" disabled={savingChild} onClick={submitEducation}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    {savingChild ? t('saving') : editId ? t('education_form.update') : t('education_form.save')}
                  </button>
                </div>
              )}

              {educationRecords.length === 0 ? (
                <div className="bg-muted rounded-xl p-10 border border-dashed border-border text-center text-muted-foreground">
                  <GraduationCap className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm">{t('education.none', { defaultValue: 'No education records on file yet.' })}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {educationRecords.map((ed, i) => (
                    <div key={ed.id ?? i} className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-xl p-6 border border-orange-100 dark:border-orange-500/20">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                            <GraduationCap className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">{ed.institution || ed.school || '—'}</h3>
                            <p className="text-sm text-muted-foreground">
                              {[ed.degree, ed.field_of_study || ed.field].filter(Boolean).join(' • ')}
                              {(ed.year || ed.graduation_year) ? ` • ${ed.year || ed.graduation_year}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => editEducation(ed)} className="text-xs text-orange-700 hover:underline">{t('actions.edit')}</button>
                          <button type="button" onClick={() => removeChild('education', ed.id)} className="text-xs text-red-600 hover:underline">{t('actions.remove')}</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{t('documents.title')}</h2>
                {isEditing && (
                  <label className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 text-sm font-medium cursor-pointer">
                    {saving ? t('documents.uploading') : t('documents.upload_document')}
                    <input type="file" className="hidden" onChange={handlePickDocument} aria-label="Upload document" />
                  </label>
                )}
              </div>

              {documents.length === 0 && !isEditing ? (
                <div className="bg-muted rounded-xl p-10 border border-dashed border-border text-center text-muted-foreground">
                  <Folder className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm">{t('documents.none', { defaultValue: 'No documents on file yet.' })}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {documents.map((doc, i) => (
                    <a
                      key={doc.id ?? i}
                      href={doc.download_url || doc.url || doc.file_url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-br from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-xl p-4 border border-orange-100 dark:border-orange-500/20 hover:shadow-md transition-all duration-200 cursor-pointer block"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <span className="px-2 py-1 bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 rounded text-xs font-medium uppercase">
                          {(doc.document_type || doc.type || 'file')}
                        </span>
                      </div>
                      <h4 className="font-semibold text-foreground text-sm mb-1">{doc.name || doc.file_name || doc.title || t('documents.default_name')}</h4>
                      {(doc.created_at || doc.uploaded_at) && (
                        <p className="text-xs text-muted-foreground">{t('documents.uploaded')}: {String(doc.created_at || doc.uploaded_at).slice(0, 10)}</p>
                      )}
                    </a>
                  ))}

                  {/* Upload New Document Card */}
                  {isEditing && (
                    <div className="bg-muted rounded-xl p-4 border-2 border-dashed border-border hover:border-orange-400 transition-all duration-200 cursor-pointer flex flex-col items-center justify-center min-h-[150px]">
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mb-3">
                        <Folder className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">{t('documents.upload_new')}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('documents.file_types')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PersonalProfile;